import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import {
  Download, Upload, Eye, Play, CheckCircle2, Clock, AlertTriangle,
  FileSpreadsheet, Lock, RefreshCw, Archive, RotateCcw, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const api = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/* ─── Result display for migration endpoints ─── */
function ResultBox({ result }) {
  if (!result) return null;
  const isError = result.error || result.status === 'validation_failed' || result.success === false;
  const summary = result.summary;

  if (summary || result.status === 'validation_failed') {
    return (
      <div data-testid="result-box" className={`mt-3 p-4 rounded border space-y-3 ${isError ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
        <p className={`text-sm font-medium ${isError ? 'text-red-800' : 'text-slate-800'}`}>{result.message}</p>
        {summary && (
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(summary).map(([k, v]) => v > 0 && (
              <span key={k} className={`inline-flex items-center gap-1 px-2 py-1 rounded font-medium ${
                k.includes('reject') || k.includes('error') ? 'bg-red-100 text-red-800' :
                k.includes('skip') || k.includes('archiv') ? 'bg-amber-100 text-amber-800' :
                'bg-emerald-100 text-emerald-800'
              }`}>{k.replace(/_/g, ' ')}: {v}</span>
            ))}
          </div>
        )}
        {result.rejected?.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-red-700 font-medium">{result.rejected.length} rejected — expand</summary>
            <div className="mt-2 max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-red-200">
                  <th className="text-left py-1 px-2">Item</th><th className="text-left py-1 px-2">Reason</th>
                </tr></thead>
                <tbody>{result.rejected.map((r, i) => (
                  <tr key={i} className="border-b border-red-100">
                    <td className="py-1 px-2 font-mono">{r.item_name || r.old_indent || r.old_po || r.old_so || r.old_wo || '—'}</td>
                    <td className="py-1 px-2">{r.reason}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </details>
        )}
        {result.report_url && (
          <a href={`${API}${result.report_url}`} download data-testid="btn-download-report"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded border border-slate-300 hover:bg-white text-slate-700">
            <FileSpreadsheet size={14}/> Download Report
          </a>
        )}
        {result.dry_run && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-medium">
            <Eye size={12}/> Dry Run — no changes made
          </span>
        )}
      </div>
    );
  }

  return (
    <div data-testid="result-box" className={`mt-3 p-3 rounded text-sm font-mono whitespace-pre-wrap border ${isError ? 'bg-red-50 border-red-200 text-red-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
      {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
    </div>
  );
}

/* ─── Action button with confirm dialog for destructive actions ─── */
function ConfirmButton({ onClick, children, variant = "default", disabled, confirmMsg, icon: Icon, size = "sm" }) {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    try { await onClick(); } finally { setLoading(false); }
  };
  const btn = (
    <Button variant={variant} size={size} disabled={disabled || loading} onClick={confirmMsg ? undefined : handleClick}
      className="gap-2 min-w-[140px]">
      {loading ? <RefreshCw size={14} className="animate-spin"/> : Icon && <Icon size={14}/>}
      {loading ? 'Processing...' : children}
    </Button>
  );
  if (!confirmMsg) return btn;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{btn}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Action</AlertDialogTitle>
          <AlertDialogDescription>{confirmMsg}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleClick}>Proceed</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ─── Status badge for each step ─── */
function StepBadge({ done, error }) {
  if (error) return <Badge variant="destructive" className="text-[10px] gap-1"><AlertTriangle size={10}/> Error</Badge>;
  if (done) return <Badge className="bg-emerald-100 text-emerald-800 text-[10px] gap-1"><CheckCircle2 size={10}/> Done</Badge>;
  return <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground"><Clock size={10}/> Not Started</Badge>;
}

/* ─── Main Page ─── */
export default function FYMigrationPage() {
  const [phases, setPhases] = useState({});
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api().get('/api/migration/status');
      setPhases(res.data.phases || {});
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const setResult = (key, data) => setResults(prev => ({ ...prev, [key]: data }));

  // ── Helpers ──
  const downloadFile = async (endpoint, key) => {
    try {
      const res = await api().get(`/api/migration/${endpoint}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `${endpoint}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const uploadFile = async (phase, key) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.xlsx,.xls';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const form = new FormData();
      form.append('file', file);
      try {
        await api().post(`/api/migration/upload/${phase}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('File uploaded');
        fetchStatus();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Upload failed');
      }
    };
    input.click();
  };

  const postAction = async (endpoint, key, dryRun) => {
    try {
      const url = `/api/migration/${endpoint}${dryRun != null ? `?dry_run=${dryRun}` : ''}`;
      const res = await api().post(url);
      setResult(key, res.data);
      if (res.data.success) toast.success(res.data.message || 'Done');
      else toast.error(res.data.message || 'Failed');
      fetchStatus();
    } catch (e) {
      setResult(key, { error: true, message: e.response?.data?.detail || 'Request failed' });
      toast.error(e.response?.data?.detail || 'Request failed');
    }
  };

  const uploadAndApply = async (endpoint, key, dryRun) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.xlsx,.xls';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const form = new FormData();
      form.append('file', file);
      try {
        const url = `/api/migration/${endpoint}${dryRun != null ? `?dry_run=${dryRun}` : ''}`;
        const res = await api().post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        setResult(key, res.data);
        if (res.data.success) toast.success(res.data.message || 'Done');
        else toast.error(res.data.message || 'Failed');
        fetchStatus();
      } catch (err) {
        setResult(key, { error: true, message: err.response?.data?.detail || 'Upload failed' });
        toast.error(err.response?.data?.detail || 'Upload failed');
      }
    };
    input.click();
  };

  // ── Section enable logic ──
  const s1 = phases.section_1_so || {};
  const s2 = phases.section_2_wo || {};
  const s3 = phases.section_3_indent_po || {};
  const s4 = phases.section_4_stock || {};
  const s5 = phases.section_5_finalize || {};
  const s6 = phases.section_6_delegation || {};
  const s7 = phases.section_7_checklist || {};

  const s2Enabled = s1.done;
  const s3Enabled = s2.done;
  const s5Enabled = s1.done && s2.done && s3.done && s4.done;

  if (loading) return <DashboardLayout title="FY Migration"><div className="p-8 text-center text-muted-foreground">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout title="FY Migration — April 2026">
      <div className="p-4 md:p-6 space-y-6 max-w-5xl">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Migrate active documents to new financial year.</p>
          <Button variant="ghost" size="sm" onClick={fetchStatus} className="gap-2"><RefreshCw size={14}/> Refresh</Button>
        </div>

        {/* ═══ Section 1: Sales Orders ═══ */}
        <Card data-testid="section-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Section 1: Sales Order Migration</CardTitle>
              <StepBadge done={s1.done}/>
            </div>
            {s1.count > 0 && <p className="text-xs text-muted-foreground">{s1.count} SOs migrated</p>}
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ConfirmButton icon={Download} onClick={() => downloadFile('generate-so-template', 'soTemplate')}>
                Download Template
              </ConfirmButton>
              <ConfirmButton icon={Upload} onClick={() => uploadFile('corrected-sos', 'soUpload')} disabled={!s1.template}>
                Upload Corrected
              </ConfirmButton>
              <ConfirmButton icon={Eye} onClick={() => postAction('create-sos', 'soDry', true)} disabled={!s1.upload}>
                Preview (Dry Run)
              </ConfirmButton>
              <ConfirmButton icon={Play} variant="default" onClick={() => postAction('create-sos', 'soLive', false)}
                disabled={!s1.upload} confirmMsg="Create new SOs from uploaded data? This will archive old SOs.">
                Create SOs
              </ConfirmButton>
            </div>
            <ResultBox result={results.soDry || results.soLive}/>
          </CardContent>
        </Card>

        {/* ═══ Section 2: Work Orders ═══ */}
        <Card data-testid="section-2" className={!s2Enabled ? 'opacity-60' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                Section 2: Work Order Migration
                {!s2Enabled && <Lock size={14} className="text-muted-foreground"/>}
              </CardTitle>
              <StepBadge done={s2.done}/>
            </div>
            {s2.count > 0 && <p className="text-xs text-muted-foreground">{s2.count} WOs migrated</p>}
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ConfirmButton icon={Download} onClick={() => downloadFile('generate-wo-mapping', 'woMapping')} disabled={!s2Enabled}>
                Download Mapping
              </ConfirmButton>
              <ConfirmButton icon={Upload} onClick={() => uploadFile('approved-wo-mapping', 'woUpload')} disabled={!s2Enabled || !s2.mapping}>
                Upload Approved
              </ConfirmButton>
              <ConfirmButton icon={Eye} onClick={() => postAction('create-wos', 'woDry', true)} disabled={!s2Enabled || !s2.upload}>
                Preview (Dry Run)
              </ConfirmButton>
              <ConfirmButton icon={Play} variant="default" onClick={() => postAction('create-wos', 'woLive', false)}
                disabled={!s2Enabled || !s2.upload} confirmMsg="Create new WOs? This will archive old WOs.">
                Create WOs
              </ConfirmButton>
            </div>
            <ResultBox result={results.woDry || results.woLive}/>
          </CardContent>
        </Card>

        {/* ═══ Section 3: Indents & Purchase Orders ═══ */}
        <Card data-testid="section-3" className={!s3Enabled ? 'opacity-60' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                Section 3: Indents & Purchase Orders
                {!s3Enabled && <Lock size={14} className="text-muted-foreground"/>}
              </CardTitle>
              <StepBadge done={s3.done}/>
            </div>
            {(s3.indent_count > 0 || s3.po_count > 0) && (
              <p className="text-xs text-muted-foreground">{s3.indent_count} indents + {s3.po_count} POs migrated</p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ConfirmButton icon={Download} onClick={() => downloadFile('generate-indent-po-template', 'indTemplate')} disabled={!s3Enabled}>
                Download Template
              </ConfirmButton>
              <ConfirmButton icon={Upload} onClick={() => uploadFile('corrected-indent-po', 'indUpload')} disabled={!s3Enabled || !s3.template}>
                Upload Corrected
              </ConfirmButton>
              <ConfirmButton icon={Eye} onClick={() => postAction('migrate-indents-pos', 'indDry', true)} disabled={!s3Enabled || !s3.upload}>
                Preview (Dry Run)
              </ConfirmButton>
              <ConfirmButton icon={Play} variant="default" onClick={() => postAction('migrate-indents-pos', 'indLive', false)}
                disabled={!s3Enabled || !s3.upload} confirmMsg="Migrate indents & POs? Old documents will be archived.">
                Migrate
              </ConfirmButton>
            </div>
            <ResultBox result={results.indDry || results.indLive}/>
          </CardContent>
        </Card>

        {/* ═══ Section 4: Stock Count ═══ */}
        <Card data-testid="section-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Section 4: Stock Count</CardTitle>
              <StepBadge done={s4.done}/>
            </div>
            <p className="text-xs text-muted-foreground">Independent — can be done in parallel with Sections 1-3</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ConfirmButton icon={Download} onClick={() => downloadFile('generate-stock-template', 'stockTemplate')}>
                Download Template
              </ConfirmButton>
              <ConfirmButton icon={Upload} onClick={() => uploadFile('stock-count', 'stockUpload')} disabled={!s4.template}>
                Upload Count
              </ConfirmButton>
              <ConfirmButton icon={Eye} onClick={() => postAction('reset-items', 'stockDry', true)} disabled={!s4.upload}>
                Preview
              </ConfirmButton>
              <ConfirmButton icon={Play} variant="default" onClick={() => postAction('reset-items', 'stockLive', false)}
                disabled={!s4.upload} confirmMsg="Apply stock count? This will reset all item counters and set opening stock.">
                Apply Stock Count
              </ConfirmButton>
            </div>
            <ResultBox result={results.stockDry || results.stockLive}/>
          </CardContent>
        </Card>

        {/* ═══ Section 5: Finalize ═══ */}
        <Card data-testid="section-5" className={!s5Enabled ? 'opacity-60' : ''}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                Section 5: Finalize
                {!s5Enabled && <Lock size={14} className="text-muted-foreground"/>}
              </CardTitle>
              <StepBadge done={s5.done}/>
            </div>
            {!s5Enabled && <p className="text-xs text-red-600">Requires Sections 1-4 to be complete</p>}
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ConfirmButton icon={Archive} onClick={() => postAction('archive', 'archive', false)}
                disabled={!s5Enabled} confirmMsg="Archive old documents? This moves pre-migration data to archive collections.">
                Archive Old Data
              </ConfirmButton>
              <ConfirmButton icon={RotateCcw} onClick={() => postAction('reset-counters', 'resetCounters', false)}
                disabled={!s5Enabled || !s5.archived} confirmMsg="Reset all document number sequences to 0?">
                Reset Doc Numbers
              </ConfirmButton>
              <ConfirmButton icon={ShieldCheck} onClick={() => postAction('verify', 'verify', false)}
                disabled={!s5Enabled || !s5.reset_counters} variant="outline">
                Verify
              </ConfirmButton>
            </div>
            <ResultBox result={results.archive || results.resetCounters || results.verify}/>
          </CardContent>
        </Card>

        {/* ═══ Section 6: Delegation Tasks ═══ */}
        <Card data-testid="section-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Section 6: Delegation Tasks Migration</CardTitle>
              <StepBadge done={s6.done}/>
            </div>
            <p className="text-xs text-muted-foreground">
              {s6.total > 0 ? `${s6.total} active tasks` : 'No active tasks'} — Independent, can be done anytime
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">Download template → Edit doer, due date, or mark DELETE=Y → Upload corrected file</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <ConfirmButton icon={Download} onClick={() => downloadFile('generate-delegation-template', 'delegTemplate')}>
                Download Template
              </ConfirmButton>
              <ConfirmButton icon={Eye} onClick={() => uploadAndApply('apply-delegation-migration', 'delegDry', true)} disabled={!s6.template}>
                Upload & Preview
              </ConfirmButton>
              <ConfirmButton icon={Play} variant="default" onClick={() => uploadAndApply('apply-delegation-migration', 'delegLive', false)}
                disabled={!s6.template} confirmMsg="Apply delegation task changes? Tasks marked DELETE will be discontinued.">
                Upload & Apply
              </ConfirmButton>
            </div>
            <ResultBox result={results.delegDry || results.delegLive}/>
          </CardContent>
        </Card>

        {/* ═══ Section 7: Checklist Tasks ═══ */}
        <Card data-testid="section-7">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Section 7: Checklist Tasks Migration</CardTitle>
              <StepBadge done={s7.done}/>
            </div>
            <p className="text-xs text-muted-foreground">
              {s7.total > 0 ? `${s7.total} active checklists` : 'No active checklists'} — Independent, can be done anytime
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">Download template → Edit doer, due date, or mark DELETE=Y → Upload corrected file</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <ConfirmButton icon={Download} onClick={() => downloadFile('generate-checklist-template', 'checkTemplate')}>
                Download Template
              </ConfirmButton>
              <ConfirmButton icon={Eye} onClick={() => uploadAndApply('apply-checklist-migration', 'checkDry', true)} disabled={!s7.template}>
                Upload & Preview
              </ConfirmButton>
              <ConfirmButton icon={Play} variant="default" onClick={() => uploadAndApply('apply-checklist-migration', 'checkLive', false)}
                disabled={!s7.template} confirmMsg="Apply checklist task changes? Tasks marked DELETE will be discontinued.">
                Upload & Apply
              </ConfirmButton>
            </div>
            <ResultBox result={results.checkDry || results.checkLive}/>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
